import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHeader from '@/components/base/PageHeader';
import MovieCard from '@/components/base/MovieCard';
import EmptyState from '@/components/base/EmptyState';
import { filterMovies, getTagCount, sortOptions, type SortKey } from '@/lib/queries';
import { useSeo } from '@/hooks/useSeo';

const PAGE_SIZE = 8;

export default function TagPage() {
  const { tagname } = useParams<{ tagname: string }>();
  const tag = tagname ?? '';
  const [sort, setSort] = useState<SortKey>('latest');
  const [visible, setVisible] = useState(PAGE_SIZE);

  const result = useMemo(() => filterMovies({ tag, sort }), [tag, sort]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [tag, sort]);

  useSeo({
    title: `${tag} - 标签影片合集 - 日韩成人电影`,
    description: `浏览日韩成人电影中带有「${tag}」标签的影片合集，共 ${result.length} 部作品，支持排序与继续加载。`,
    keywords: `${tag},日韩成人电影,${tag}影片,标签筛选,成人影片推荐`,
  });

  return (
    <>
      <PageHeader
        eyebrow="Tag collection"
        title={`标签：${tag}`}
        description={`这里汇总了站内所有带有「${tag}」标签的影片。标签是跨分类的，所以你会看到剧情、悬疑、都市等不同题材的作品混排在一起。`}
        crumbs={[{ label: '标签筛选' }, { label: tag }]}
        meta={
          <>
            <span className="flex items-center gap-1.5">
              <i className="ri-price-tag-3-line text-sm text-primary-300" />
              共 {getTagCount(tag)} 部关联影片
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-stack-line text-sm text-primary-300" />
              标签跨分类聚合
            </span>
          </>
        }
      />

      <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-800 bg-background-900 px-4 py-3">
          <p className="text-xs text-foreground-500">
            当前结果 <span className="text-foreground-100">{result.length}</span> 部影片
          </p>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 text-xs text-foreground-600">
              <i className="ri-sort-desc text-sm" />
              排序
            </span>
            <div className="flex gap-1 rounded-md border border-background-800 bg-background-950 p-1">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSort(option.key)}
                  className={`cursor-pointer whitespace-nowrap rounded px-3 py-1.5 text-xs transition-colors ${
                    sort === option.key
                      ? 'bg-accent-500 font-medium text-background-950'
                      : 'text-foreground-400 hover:text-foreground-100'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {result.length === 0 ? (
          <div className="mt-8">
            <EmptyState
              icon="ri-price-tag-3-line"
              title="这个标签下暂时没有影片"
              description="标签可能已经更名，或者还没有关联的影片。你可以返回首页，或者进入分类页继续浏览。"
              primaryLabel="浏览全部分类"
              primaryTo="/"
            />
          </div>
        ) : (
          <>
            <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-x-5 lg:grid-cols-4 lg:gap-x-6">
              {result.slice(0, visible).map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>

            <div className="mt-10 flex flex-col items-center gap-4">
              <p className="text-xs text-foreground-600">
                已显示 {Math.min(visible, result.length)} / {result.length} 部影片
              </p>
              {visible < result.length && (
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                  className="cursor-pointer whitespace-nowrap rounded-md border border-background-700 px-6 py-2.5 text-sm text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400"
                >
                  继续加载
                </button>
              )}
              <Link
                to="/search"
                className="flex items-center gap-1.5 text-xs text-foreground-500 transition-colors hover:text-accent-400"
              >
                <i className="ri-search-line text-sm" />
                换一个关键词搜索
              </Link>
            </div>
          </>
        )}
      </div>
    </>
  );
}